import { Fragment, type ReactNode } from 'react';

/**
 * Hardened, dependency-free markdown renderer shared across AI surfaces (the
 * intelligence chat, Knowledgebase Q&A, AI note summaries). Replaces the
 * per-surface hand-rolled parsers that silently dumped tables, code fences,
 * blockquotes, deep headings, nested/`1)` lists and `---` rules as literal text.
 *
 * Supported block constructs: ATX headings (#–######), unordered lists
 * (`-`/`*`/`•`, nested by indentation), ordered lists (`1.` and `1)`),
 * blockquotes, fenced code blocks (``` ```), GFM tables, horizontal rules
 * (`---`/`***`/`___`) and paragraphs.
 *
 * Supported inline constructs: `**bold**`, `*italic*`/`_italic_`,
 * `***bold italic***`, `` `code` ``, `~~strike~~`, and `[label](url)` links
 * (rendered via a pluggable {@link LinkPolicy} so callers can whitelist safe
 * destinations).
 *
 * Streaming: pass `streaming` to optimistically auto-close a trailing unmatched
 * `**` / `` ` `` / `~~` so a half-streamed `**bol` renders as bold immediately
 * instead of flashing literal asterisks until the closing marker arrives.
 */

/** How a link URL should be treated: as an in-app link, an external link, or
 * dropped to label-only text when it isn't safe to render as an anchor. */
export type LinkKind = 'app' | 'external' | null;
export type LinkPolicy = (url: string) => LinkKind;

const defaultLinkPolicy: LinkPolicy = (url) => {
  if (/^(https?:|mailto:|tel:)/i.test(url)) return 'external';
  if (url.startsWith('/')) return 'app';
  return null;
};

const LINK_CLASS =
  'text-blue-600 underline underline-offset-2 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300';

// One combined matcher, ordered so multi-char markers win over their prefixes
// (link → ***bolditalic*** → **bold** → *italic*/_italic_ → ~~strike~~ → `code`).
const INLINE_RE =
  /(\[[^\]]+\]\([^)\s]+\)|\*\*\*[^*]+?\*\*\*|\*\*[^*]+?\*\*|\*[^*\n]+?\*|_[^_\n]+?_|~~[^~]+?~~|`[^`]+?`)/g;

const LINK_RE = /^\[([^\]]+)\]\(([^)\s]+)\)$/;

/**
 * During streaming, append closers for a trailing unbalanced `**` / `` ` `` /
 * `~~` so emphasis renders optimistically instead of showing raw markers.
 */
function balanceStreaming(text: string): string {
  let out = text;
  if ((out.match(/`/g)?.length ?? 0) % 2 === 1) out += '`';
  if ((out.match(/\*\*/g)?.length ?? 0) % 2 === 1) out += '**';
  if ((out.match(/~~/g)?.length ?? 0) % 2 === 1) out += '~~';
  return out;
}

function renderInline(
  text: string,
  linkPolicy: LinkPolicy,
  keyPrefix: string,
  streaming?: boolean
): ReactNode[] {
  const source = streaming ? balanceStreaming(text) : text;
  const parts = source.split(INLINE_RE);
  return parts.map((part, i) => {
    const key = `${keyPrefix}-${i}`;
    if (!part) return null;

    if (part.startsWith('***') && part.endsWith('***') && part.length > 6) {
      return (
        <strong key={key}>
          <em>{renderInline(part.slice(3, -3), linkPolicy, key, streaming)}</em>
        </strong>
      );
    }
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return (
        <strong key={key}>{renderInline(part.slice(2, -2), linkPolicy, key, streaming)}</strong>
      );
    }
    if (part.startsWith('~~') && part.endsWith('~~') && part.length > 4) {
      return (
        <del key={key} className="text-gray-500 dark:text-gray-400">
          {renderInline(part.slice(2, -2), linkPolicy, key, streaming)}
        </del>
      );
    }
    if (part.startsWith('`') && part.endsWith('`') && part.length > 2) {
      return (
        <code
          key={key}
          className="rounded bg-gray-200 px-1 py-0.5 font-mono text-[0.85em] dark:bg-gray-700"
        >
          {part.slice(1, -1)}
        </code>
      );
    }
    const linkMatch = LINK_RE.exec(part);
    if (linkMatch) {
      const label = linkMatch[1] ?? '';
      const url = linkMatch[2] ?? '';
      const kind = linkPolicy(url);
      if (kind) {
        return (
          <a key={key} href={url} target="_blank" rel="noopener noreferrer" className={LINK_CLASS}>
            {label}
          </a>
        );
      }
      // Unsafe / unknown destination — render the label only, never the raw syntax.
      return <Fragment key={key}>{label}</Fragment>;
    }
    if (
      (part.startsWith('*') && part.endsWith('*') && part.length > 2) ||
      (part.startsWith('_') && part.endsWith('_') && part.length > 2)
    ) {
      return <em key={key}>{renderInline(part.slice(1, -1), linkPolicy, key, streaming)}</em>;
    }
    return <Fragment key={key}>{part}</Fragment>;
  });
}

const HEADING_RE = /^(#{1,6})\s+(.*)$/;
const UL_RE = /^(\s*)[-*•]\s+(.*)$/;
const OL_RE = /^(\s*)(\d+)[.)]\s+(.*)$/;
const HR_RE = /^\s*([-*_])(\s*\1){2,}\s*$/;
const FENCE_RE = /^\s*```/;
const QUOTE_RE = /^\s*>\s?(.*)$/;

function isTableSeparator(line: string): boolean {
  return /^\s*\|?\s*:?-{1,}:?\s*(\|\s*:?-{1,}:?\s*)+\|?\s*$/.test(line) && line.includes('-');
}
function looksLikeTableRow(line: string): boolean {
  return line.includes('|') && line.trim().length > 0;
}
function splitTableRow(line: string): string[] {
  let trimmed = line.trim();
  if (trimmed.startsWith('|')) trimmed = trimmed.slice(1);
  if (trimmed.endsWith('|')) trimmed = trimmed.slice(0, -1);
  return trimmed.split('|').map((c) => c.trim());
}

interface ListItemNode {
  indent: number;
  ordered: boolean;
  text: string;
}

/** Build nested <ul>/<ol> from a flat list of items keyed by leading indent. */
function renderListTree(
  items: ListItemNode[],
  start: number,
  baseIndent: number,
  linkPolicy: LinkPolicy,
  isMobile: boolean | undefined,
  streaming: boolean | undefined,
  keyPrefix: string
): { node: ReactNode; next: number } {
  const ordered = items[start]?.ordered ?? false;
  const children: ReactNode[] = [];
  let i = start;
  let liKey = 0;
  while (i < items.length && items[i]!.indent >= baseIndent) {
    const item = items[i]!;
    if (item.indent > baseIndent) {
      // Defensive: a deeper item with no parent at this level — attach as its own nest.
      const nested = renderListTree(
        items,
        i,
        item.indent,
        linkPolicy,
        isMobile,
        streaming,
        `${keyPrefix}-${liKey}`
      );
      children.push(nested.node);
      i = nested.next;
      continue;
    }
    const liChildren: ReactNode[] = [
      <Fragment key="t">
        {renderInline(item.text, linkPolicy, `${keyPrefix}-${liKey}`, streaming)}
      </Fragment>,
    ];
    i += 1;
    // Consume any deeper-indented items as a nested list under this <li>.
    if (i < items.length && items[i]!.indent > baseIndent) {
      const nested = renderListTree(
        items,
        i,
        items[i]!.indent,
        linkPolicy,
        isMobile,
        streaming,
        `${keyPrefix}-${liKey}n`
      );
      liChildren.push(<Fragment key="n">{nested.node}</Fragment>);
      i = nested.next;
    }
    children.push(<li key={liKey}>{liChildren}</li>);
    liKey += 1;
  }
  const spacing = isMobile ? 'space-y-2' : 'space-y-1';
  const node = ordered ? (
    <ol key={keyPrefix} className={`list-decimal pl-5 ${spacing}`}>
      {children}
    </ol>
  ) : (
    <ul key={keyPrefix} className={`list-disc pl-5 marker:text-gray-400 ${spacing}`}>
      {children}
    </ul>
  );
  return { node, next: i };
}

interface MarkdownContentProps {
  content: string;
  /** Wrapper className — controls block spacing and line-height. */
  className?: string;
  /** Larger typography + spacing for full-screen mobile surfaces. */
  isMobile?: boolean;
  /** Optimistically close dangling emphasis markers mid-stream (no flicker). */
  streaming?: boolean;
  /** Decide how each link URL is rendered. Defaults to http(s)/mailto/tel + `/`. */
  linkPolicy?: LinkPolicy;
}

export function MarkdownContent({
  content,
  className,
  isMobile,
  streaming,
  linkPolicy = defaultLinkPolicy,
}: MarkdownContentProps) {
  const lines = content.split('\n');
  const nodes: ReactNode[] = [];
  let i = 0;
  let block = 0;

  while (i < lines.length) {
    const line = lines[i] ?? '';
    const key = `b-${block++}`;

    // Fenced code block.
    if (FENCE_RE.test(line)) {
      const code: string[] = [];
      i += 1;
      while (i < lines.length && !FENCE_RE.test(lines[i] ?? '')) {
        code.push(lines[i] ?? '');
        i += 1;
      }
      i += 1; // closing fence (or EOF while streaming)
      nodes.push(
        <pre
          key={key}
          className="overflow-x-auto rounded-lg bg-gray-100 p-3 text-xs dark:bg-gray-800/80"
        >
          <code className="font-mono text-gray-800 dark:text-gray-200">{code.join('\n')}</code>
        </pre>
      );
      continue;
    }

    // Horizontal rule (checked before lists so `***`/`---` rules don't read as bullets).
    if (HR_RE.test(line)) {
      nodes.push(<hr key={key} className="my-2 border-gray-200 dark:border-gray-700" />);
      i += 1;
      continue;
    }

    // Heading.
    const heading = HEADING_RE.exec(line);
    if (heading) {
      const level = heading[1]?.length ?? 1;
      const text = heading[2] ?? '';
      const cls =
        level <= 2
          ? isMobile
            ? 'text-[16px] font-bold leading-tight mt-3 mb-0.5'
            : 'text-sm font-bold mt-2 mb-0.5'
          : 'text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mt-2';
      nodes.push(
        <p key={key} className={cls}>
          {renderInline(text, linkPolicy, key, streaming)}
        </p>
      );
      i += 1;
      continue;
    }

    // GFM table: a row line immediately followed by a `---|---` separator.
    if (looksLikeTableRow(line) && isTableSeparator(lines[i + 1] ?? '')) {
      const header = splitTableRow(line);
      i += 2; // header + separator
      const rows: string[][] = [];
      while (
        i < lines.length &&
        looksLikeTableRow(lines[i] ?? '') &&
        !isTableSeparator(lines[i] ?? '')
      ) {
        rows.push(splitTableRow(lines[i] ?? ''));
        i += 1;
      }
      nodes.push(
        <div key={key} className="overflow-x-auto">
          <table className="w-full border-collapse text-xs">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                {header.map((h, hi) => (
                  <th key={hi} className="px-2 py-1 text-left font-semibold">
                    {renderInline(h, linkPolicy, `${key}-h-${hi}`, streaming)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, ri) => (
                <tr key={ri} className="border-b border-gray-100 dark:border-gray-800">
                  {header.map((_, ci) => (
                    <td key={ci} className="px-2 py-1 align-top">
                      {renderInline(row[ci] ?? '', linkPolicy, `${key}-r-${ri}-${ci}`, streaming)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    // Blockquote (one or more consecutive `>` lines).
    if (QUOTE_RE.test(line)) {
      const quoted: string[] = [];
      while (i < lines.length && QUOTE_RE.test(lines[i] ?? '')) {
        quoted.push(QUOTE_RE.exec(lines[i] ?? '')?.[1] ?? '');
        i += 1;
      }
      nodes.push(
        <blockquote
          key={key}
          className="border-l-2 border-gray-300 pl-3 text-gray-600 italic dark:border-gray-600 dark:text-gray-300"
        >
          {renderInline(quoted.join(' '), linkPolicy, key, streaming)}
        </blockquote>
      );
      continue;
    }

    // Lists (unordered or ordered, with nesting by indent).
    if (UL_RE.test(line) || OL_RE.test(line)) {
      const items: ListItemNode[] = [];
      while (i < lines.length) {
        const ul = UL_RE.exec(lines[i] ?? '');
        const ol = ul ? null : OL_RE.exec(lines[i] ?? '');
        if (ul) {
          items.push({ indent: (ul[1] ?? '').length, ordered: false, text: ul[2] ?? '' });
        } else if (ol) {
          items.push({ indent: (ol[1] ?? '').length, ordered: true, text: ol[3] ?? '' });
        } else {
          break;
        }
        i += 1;
      }
      const baseIndent = Math.min(...items.map((it) => it.indent));
      const { node } = renderListTree(items, 0, baseIndent, linkPolicy, isMobile, streaming, key);
      nodes.push(<Fragment key={key}>{node}</Fragment>);
      continue;
    }

    // Paragraph (skip blank lines).
    if (line.trim()) {
      nodes.push(
        <p key={key} className="whitespace-pre-wrap">
          {renderInline(line, linkPolicy, key, streaming)}
        </p>
      );
    }
    i += 1;
  }

  return (
    <div
      className={className ?? (isMobile ? 'space-y-3 leading-[1.65]' : 'space-y-1.5 leading-snug')}
    >
      {nodes}
    </div>
  );
}
